
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Info, Plus } from 'lucide-react';
import { MultiSelect } from '@/components/ui/multi-select';
import { ContentQueryInput } from './ContentQueryInput';
import TwitterKolSelector from './TwitterKolSelector';

interface DeliverableFormProps {
  onSubmit: (data: DeliverableFormData) => void;
  onCancel: () => void;
  initialData?: Partial<DeliverableFormData>;
  isEditing?: boolean;
  inCampaignCreation?: boolean;
  showButtons?: boolean;
}

interface DeliverableFormData {
  name: string;
  content_query: string;
  type: string;
  startDate: Date | null;
  endDate: Date | null;
  kolUsernames: string[];
}

const DeliverableForm: React.FC<DeliverableFormProps> = ({ 
  onSubmit, 
  onCancel,
  initialData = {},
  isEditing = false,
  inCampaignCreation = false,
  showButtons = true
}) => {
  const [formData, setFormData] = useState<DeliverableFormData>({
    name: initialData.name || '',
    content_query: initialData.content_query || '',
    type: initialData.type || 'any',
    startDate: initialData.startDate || null,
    endDate: initialData.endDate || null,
    kolUsernames: initialData.kolUsernames || [],
  });
  
  const [isFormValid, setIsFormValid] = useState<boolean>(true);
  const [contentQueryValid, setContentQueryValid] = useState<boolean>(true);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleContentQueryChange = (value: string) => {
    setFormData(prev => ({ ...prev, content_query: value }));
  };
  
  const handleQueryValidationChange = (isValid: boolean) => {
    setContentQueryValid(isValid);
  };
  
  const handleTypeChange = (value: string) => {
    setFormData(prev => ({ ...prev, type: value }));
  };
  
  const handleDateChange = (name: 'startDate' | 'endDate', date: Date | null) => {
    setFormData(prev => ({ ...prev, [name]: date }));
  };
  
  const handleKolSelectChange = (selected: string[]) => {
    setFormData(prev => ({
      ...prev,
      kolUsernames: selected
    }));
  };
  
  // Check form validity
  useEffect(() => {
    const formIsValid = 
      !!formData.name &&
      !!formData.content_query &&
      contentQueryValid;
    
    setIsFormValid(formIsValid);
  }, [formData.name, formData.content_query, contentQueryValid]);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid) {
      onSubmit(formData);
    }
  };
  
  const getButtonLabel = () => {
    if (isEditing) return 'Update Deliverable';
    if (inCampaignCreation) return 'Add to Campaign';
    return 'Create Deliverable';
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Deliverable Name</Label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="Enter a name for this deliverable"
          required
        />
      </div>
      
      {/* Twitter KOL Selector Component - moved after Deliverable Name */}
      <TwitterKolSelector
        selectedKols={formData.kolUsernames}
        onChange={handleKolSelectChange}
      />
      
      <div className="space-y-2">
        <Label htmlFor="content_query">Search Query</Label>
        <ContentQueryInput 
          value={formData.content_query}
          onChange={handleContentQueryChange}
          onValidationChange={handleQueryValidationChange}
        />
        <div className="flex items-start text-xs text-muted-foreground mt-1">
          <Info className="h-3.5 w-3.5 mr-1 mt-0.5 flex-shrink-0" />
          <span>
            Use Twitter search syntax. For example, use OR for alternatives, quotes for exact phrases, 
            -is:retweet to exclude retweets. <a href="https://developer.twitter.com/en/docs/twitter-api/tweets/search/integrate/build-a-query" target="_blank" rel="noopener noreferrer" className="underline">Learn more</a>
          </span>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Start Date</Label>
          <DatePicker
            date={formData.startDate}
            setDate={(date) => handleDateChange('startDate', date)}
            placeholder="Select start date"
          />
        </div>
        
        <div className="space-y-2">
          <Label>End Date</Label>
          <DatePicker
            date={formData.endDate}
            setDate={(date) => handleDateChange('endDate', date)}
            placeholder="Select end date"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="type">Tweet Type</Label>
        <Select value={formData.type} onValueChange={handleTypeChange}>
          <SelectTrigger id="type">
            <SelectValue placeholder="Select tweet type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any Tweet</SelectItem>
            <SelectItem value="original">Original Tweets Only</SelectItem>
            <SelectItem value="retweets">Retweets Only</SelectItem>
            <SelectItem value="quotes">Quote Tweets Only</SelectItem>
            <SelectItem value="replies">Replies Only</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {showButtons && (
        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={!isFormValid}
          >
            {getButtonLabel()}
          </Button>
        </div>
      )}
    </form>
  );
};

export default DeliverableForm;
